import { useState, useRef } from "react";

const MODES = {
  FRESH: "fresh",
  REWRITE: "rewrite",
  REVISE: "revise",
};

const LANGUAGES = ["UK English", "US English"];
const WORD_COUNTS = ["400", "600", "800", "1000", "1200", "1500"];

function buildSystemPrompt(date) {
  return `You are an experienced recruitment consultant with deep expertise in your specialisation. You write blogs for a recruitment agency directed at professionals in your field — people who know their industry well. Your writing voice is relaxed, confident and insightful. You're passionate about the field without being promotional. You speak to peers, not pupils.

Today's date is: ${date}

Your writing rules, non-negotiable:
- Use contractions and natural phrasing throughout
- Mix sentence lengths for rhythm — short punchy sentences alongside longer ones
- Address the reader directly with "you" language
- Use subheadings that sound like something a person would say, not an academic title
- Include rhetorical questions to pull the reader in
- Ground everything in real-world scenarios the reader will recognise
- Be honest about challenges without being discouraging
- Any statistics, salaries, market data or trends must reflect the current date above
- Ignore any dates, statistics or figures from any reference material provided — replace with current accurate data
- Do not use emoji or em dashes
- Do not use corporate jargon or cliched expressions like "in today's fast-paced world" or "in the world of"
- Do not quote or reference any other recruitment agencies
- Do not write a section titled "Conclusion" — end naturally
- Do not patronise the reader — assume they know their field better than you do
- Write in the language variant specified in each request — apply it consistently to spelling, vocabulary and phrasing. Never mix variants within the same blog

HUMAN AUTHENTICITY — THIS IS THE MOST IMPORTANT SECTION. FAILURE HERE MEANS THE BLOG FAILS:
The single biggest giveaway of AI writing is smoothness. Every section doing its job perfectly. Every paragraph landing cleanly. No rough edges. You must deliberately break this pattern.

- Do NOT write a clean arc. Real bloggers meander. One section should do two things at once, or veer slightly off topic before pulling back
- NEVER use the word "genuinely" — it is the single most common AI tell word
- NEVER preemptively address scepticism with phrases like "this time it's different" or "this isn't hype" — that rehearsed quality is an immediate AI signal
- Include one opinion that goes slightly too far — something a cautious editor would want to soften. Real people have edges
- Add one oddly specific detail that only someone with real experience would know — a specific site, an obscure programme name, a precise figure that feels lived-in rather than researched
- Vary paragraph lengths dramatically and unpredictably — one paragraph must be a single sentence. One must be noticeably longer than everything else
- Let one paragraph trail off without a clean resolution — real writers do not always land every point
- Salary or market data must be woven into the narrative, not presented as a standalone paragraph. It should feel like something mentioned in passing, not a prompted insert
- The closing must not feel like a CTA. It should feel like the writer ran out of steam in a natural way and mentioned getting in touch almost as an afterthought
- Include at least one sentence that is slightly too long or structured awkwardly — the kind a copy editor would flag but a real writer would leave in
- Read the output back before returning it. If every section lands cleanly and there are no rough edges, rewrite until there are`;
}

function buildUserPrompt(mode, fields) {
  const { topic, audience, wordCount, language, notes, referenceBlog, originalBlog, revisionInstructions, sourceMaterial, recruitmentSection } = fields;
  const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  if (mode === MODES.REVISE) {
    return `Language variant: ${language}

REVISION REQUEST: ${revisionInstructions}

Original blog to revise:
${originalBlog}

Instructions for revision:
- Do not write a new blog — only revise the original above
- Read the revision instructions carefully and apply exactly what has been asked
- Keep everything that isn't mentioned in the revision instructions intact
- Maintain the language variant specified above consistently throughout
- Return the full revised blog, not just the changed sections

Return the output in this exact format:
Title:
Meta description (155 characters max):
Blog body:`;
  }

  const sourceMaterialBlock = (mode === MODES.FRESH && sourceMaterial)
    ? `SOURCE MATERIAL:
${sourceMaterial}

Instructions for source material:
- Extract relevant facts, data points, angles and information from this source
- Use this information to enrich the blog — weave it in naturally
- Do not copy sentences or phrasing from the source
- Any stats, figures or dates in the source should be verified against current knowledge — update anything that may be outdated as of ${today}
- The final blog must read as completely original, written in your own voice`
    : "";

  const referenceBlock = referenceBlog
    ? `REFERENCE BLOG:
${referenceBlog}

Instructions for the reference blog:
- Use it as inspiration only — do not copy sentences, structure, or phrasing
- Extract the core topic, useful angles, and subject matter
- Write an entirely new blog from scratch in your own voice
- Treat any stats, figures, salaries or years in the source as outdated — replace with current data as of ${today}`
    : "";

  return `Write a blog post for a recruitment agency with the following details:

Topic: ${topic || "Extract from reference blog"}
Target audience: ${audience || "Extract from reference blog"}
Approximate word count: ${wordCount}
Language variant: ${language}
Specific angles or points to cover: ${notes || "None"}

${sourceMaterialBlock}
${referenceBlock}

${recruitmentSection ? `RECRUITMENT SECTION REQUIRED:
- Weave a dedicated section into the blog that naturally connects the topic to recruitment and careers
- Cover: what the current hiring market looks like in this field, what skills employers are actively seeking right now, salary ranges or contract rates where relevant
- This section should feel like a natural part of the blog, not a bolt-on
- Base all figures and market commentary on the current date: ${today}
- Do not make it sound like a job advert — keep the peer-to-peer voice` : ""}

Structure the blog as follows:
- A strong opening that does not start with "I", "In the world of", or a dictionary definition — hook the reader immediately
- 3 to 5 subheadings that carry the narrative forward naturally
- A closing paragraph (no "Conclusion" heading) that ends with a clear, warm call to action inviting the reader to get in touch

Return the output in this exact format:
Title:
Meta description (155 characters max):
Blog body:`;
}

function buildInlineRevisePrompt(currentBlog, instructions, language) {
  return `Language variant: ${language}

REVISION REQUEST: ${instructions}

Original blog to revise:
${currentBlog}

Instructions for revision:
- Do not write a new blog — only revise the original above
- Read the revision instructions carefully and apply exactly what has been asked
- Keep everything that is not mentioned in the revision instructions intact
- Maintain the language variant specified above consistently throughout
- Return the full revised blog, not just the changed sections

Return the output in this exact format:
Title:
Meta description (155 characters max):
Blog body:`;
}

function parseBlogOutput(raw) {
  const titleMatch = raw.match(/Title:\s*(.+)/);
  const metaMatch = raw.match(/Meta description[^:]*:\s*(.+)/);
  const bodyMatch = raw.match(/Blog body:\s*([\s\S]+)/);
  return {
    title: titleMatch ? titleMatch[1].trim() : "",
    meta: metaMatch ? metaMatch[1].trim() : "",
    body: bodyMatch ? bodyMatch[1].trim() : raw,
  };
}

function downloadWord(blog) {
  const { title, meta, body } = blog;
  const esc = s => s
    .replace(/\\/g, "\\\\")
    .replace(/\{/g, "\\{")
    .replace(/\}/g, "\\}")
    .replace(/[\u0080-\uFFFF]/g, c => "\\u" + c.charCodeAt(0) + "?");
  let rtf = "{\\rtf1\\ansi\\deff0\n";
  rtf += "{\\fonttbl{\\f0\\froman\\fcharset0 Calibri;}}\n";
  rtf += "\\widowctrl\\wpaper15840\\wpaperw12240\\margl1800\\margr1800\\margt1440\\margb1440\n";
  rtf += "{\\pard\\sb240\\sa120\\b\\f0\\fs36 " + esc(title) + "\\par}\n";
  rtf += "{\\pard\\sb0\\sa240\\i\\f0\\fs18 Meta: " + esc(meta) + "\\par}\n";
  body.split(/\n\n+/).forEach(para => {
    const p = para.trim();
    if (!p) return;
    if (p.startsWith("## ") || p.startsWith("# ")) {
      rtf += "{\\pard\\sb240\\sa120\\b\\f0\\fs28 " + esc(p.replace(/^#{1,2} /,"")) + "\\par}\n";
    } else {
      rtf += "{\\pard\\sb0\\sa160\\f0\\fs22 " + esc(p.replace(/\n/g," ")) + "\\par}\n";
    }
  });
  rtf += "}";
  const blob = new Blob([rtf], { type: "application/rtf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = title.replace(/[^a-z0-9]/gi, "_").toLowerCase() + ".rtf";
  a.click();
  URL.revokeObjectURL(url);
}

// Inline revision panel shown below the blog output
function InlineRevise({ rawOutput, language, onRevised }) {
  const [instructions, setInstructions] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [revisionCount, setRevisionCount] = useState(0);

  const handleRevise = async () => {
    if (!instructions.trim()) return setError("Please describe what you'd like changed.");
    setError("");
    setLoading(true);
    const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    try {
      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": API_KEY,
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model: "claude-opus-4-6",
          max_tokens: 4000,
          system: buildSystemPrompt(today),
          messages: [{ role: "user", content: buildInlineRevisePrompt(rawOutput, instructions, language) }],
        }),
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error.message);
      onRevised(data.content[0].text);
      setInstructions("");
      setRevisionCount(c => c + 1);
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: "#fff", borderRadius: 14, padding: 28, boxShadow: "0 2px 16px rgba(0,0,0,0.07)", border: "1.5px dashed #38bdf8", marginBottom: 40 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <div style={{ width: 28, height: 28, background: "#0ea5e9", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ color: "#fff", fontSize: 14 }}>✎</span>
        </div>
        <div>
          <div style={{ fontFamily: "sans-serif", fontSize: 13, fontWeight: 700, color: "#1a1a2e" }}>
            Revise this blog
            {revisionCount > 0 && <span style={{ marginLeft: 8, background: "#0ea5e9", color: "#fff", borderRadius: 10, padding: "1px 8px", fontSize: 11 }}>{revisionCount} revision{revisionCount > 1 ? "s" : ""} applied</span>}
          </div>
          <div style={{ fontFamily: "sans-serif", fontSize: 12, color: "#999" }}>Describe exactly what you want changed — the rest stays as is</div>
        </div>
      </div>

      <textarea
        rows={3}
        placeholder='e.g. "The opening feels too flat, make it more direct" or "Shorten the third section" or "The call to action is too pushy, soften it"'
        value={instructions}
        onChange={e => setInstructions(e.target.value)}
        style={{
          width: "100%", padding: "11px 14px", borderRadius: 9, border: "1.5px solid #ddd",
          fontSize: 14, fontFamily: "sans-serif", outline: "none", color: "#1a1a2e",
          background: "#fafafa", resize: "vertical", boxSizing: "border-box", marginBottom: 12,
        }}
      />

      {error && (
        <div style={{ background: "#fff4f4", border: "1px solid #f5c6c6", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontFamily: "sans-serif", fontSize: 13, color: "#c0392b" }}>
          {error}
        </div>
      )}

      <button
        onClick={handleRevise}
        disabled={loading}
        style={{
          padding: "11px 28px", background: loading ? "#aaa" : "#0ea5e9",
          color: "#fff", border: "none", borderRadius: 9, fontFamily: "sans-serif",
          fontSize: 14, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
          transition: "background 0.15s",
        }}
      >
        {loading ? "Revising..." : "Apply Revision"}
      </button>

      {loading && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginLeft: 14, fontFamily: "sans-serif", fontSize: 13, color: "#888" }}>
          <div style={{ width: 16, height: 16, border: "2px solid #e8e4dc", borderTop: "2px solid #38bdf8", borderRadius: "50%", animation: "spin 0.9s linear infinite" }} />
          Updating your blog...
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
    </div>
  );
}

const API_KEY = "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";

export default function BlogWriter() {
  const [mode, setMode] = useState(MODES.FRESH);
  const [fields, setFields] = useState({
    topic: "", audience: "", wordCount: "800", language: "UK English",
    notes: "", referenceBlog: "", originalBlog: "", revisionInstructions: "", sourceMaterial: "", recruitmentSection: false,
  });
  const [loading, setLoading] = useState(false);
  const [blog, setBlog] = useState(null);
  const [error, setError] = useState("");
  const [rawOutput, setRawOutput] = useState("");
  const resultRef = useRef(null);

  const set = (key, val) => setFields(f => ({ ...f, [key]: val }));

  const handleGenerate = async () => {
    if (mode === MODES.REVISE && !fields.originalBlog) return setError("Please paste the blog you want to revise.");
    if (mode === MODES.REWRITE && !fields.referenceBlog) return setError("Please paste a reference blog.");
    setError("");
    setLoading(true);
    setBlog(null);

    const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

    try {
      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": API_KEY,
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model: "claude-opus-4-6",
          max_tokens: 4000,
          system: buildSystemPrompt(today),
          messages: [{ role: "user", content: buildUserPrompt(mode, fields) }],
        }),
      });

      const data = await response.json();
      if (data.error) throw new Error(data.error.message);
      const raw = data.content[0].text;
      setRawOutput(raw);
      setBlog(parseBlogOutput(raw));
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const modeLabels = {
    [MODES.FRESH]: "Write Fresh Blog",
    [MODES.REWRITE]: "Rewrite from Source",
    [MODES.REVISE]: "Revise Existing Blog",
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f7f5f0", fontFamily: "'Georgia', serif" }}>
      {/* Header */}
      <div style={{ background: "#000000", padding: "28px 40px", display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ width: 38, height: 38, background: "#38bdf8", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ color: "#000", fontWeight: 900, fontSize: 18, fontFamily: "sans-serif" }}>A</span>
        </div>
        <div>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 18, letterSpacing: 0.3 }}>AIVAN</div>
          <div style={{ color: "#38bdf8", fontSize: 12, fontFamily: "sans-serif", letterSpacing: 1, textTransform: "uppercase" }}>AI Blog Writer</div>
        </div>
      </div>

      <div style={{ maxWidth: 780, margin: "0 auto", padding: "36px 24px 60px" }}>

        {/* API Key */}
        {/* Mode Tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 28 }}>
          {Object.entries(modeLabels).map(([key, label]) => (
            <button
              key={key}
              onClick={() => { setMode(key); setBlog(null); setError(""); }}
              style={{
                flex: 1, padding: "12px 8px", borderRadius: 10, border: "2px solid",
                borderColor: mode === key ? "#0ea5e9" : "#ddd",
                background: mode === key ? "#0ea5e9" : "#fff",
                color: mode === key ? "#fff" : "#555",
                fontFamily: "sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Form Card */}
        <div style={{ background: "#fff", borderRadius: 14, padding: 32, boxShadow: "0 2px 16px rgba(0,0,0,0.07)", border: "1px solid #e8e4dc", marginBottom: 24 }}>

          {/* Language + Word Count row */}
          <div style={{ display: "flex", gap: 16, marginBottom: 22 }}>
            <div style={{ flex: 1 }}>
              <Label>Language</Label>
              <select value={fields.language} onChange={e => set("language", e.target.value)} style={selectStyle}>
                {LANGUAGES.map(l => <option key={l}>{l}</option>)}
              </select>
            </div>
            {mode !== MODES.REVISE && (
              <div style={{ flex: 1 }}>
                <Label>Word Count</Label>
                <select value={fields.wordCount} onChange={e => set("wordCount", e.target.value)} style={selectStyle}>
                  {WORD_COUNTS.map(w => <option key={w}>{w}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* FRESH mode */}
          {mode === MODES.FRESH && (
            <>
              <Field label="Blog Topic" placeholder="e.g. Why interim finance directors are in high demand right now" value={fields.topic} onChange={v => set("topic", v)} />
              <Field label="Target Audience" placeholder="e.g. CFOs and senior finance professionals" value={fields.audience} onChange={v => set("audience", v)} />
              
              <div onClick={() => set("recruitmentSection", !fields.recruitmentSection)} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 16px", background: fields.recruitmentSection ? "#f0f4ff" : "#fafafa", border: `1.5px solid ${fields.recruitmentSection ? "#4a6fa5" : "#ddd"}`, borderRadius: 10, cursor: "pointer", marginBottom: 20, transition: "all 0.15s" }}><div style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${fields.recruitmentSection ? "#4a6fa5" : "#bbb"}`, background: fields.recruitmentSection ? "#4a6fa5" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>{fields.recruitmentSection && <span style={{ color: "#fff", fontSize: 12, fontWeight: 900 }}>✓</span>}</div><div><div style={{ fontFamily: "sans-serif", fontSize: 13, fontWeight: 700, color: "#1a1a2e" }}>Include Recruitment Section</div><div style={{ fontFamily: "sans-serif", fontSize: 12, color: "#777", marginTop: 2 }}>Adds a natural section covering hiring trends, in-demand skills, and salary ranges relevant to this topic</div></div></div>
              <Field label="Angles or Points to Cover (optional)" placeholder="Any specific themes, stats, or talking points you want included" value={fields.notes} onChange={v => set("notes", v)} textarea rows={3} />
              <div style={{ borderTop: "1px dashed #e0dbd0", margin: "8px 0 20px" }} />
              <Field
                label="Source Material (optional)"
                placeholder="Paste any article, webpage or content you'd like the AI to draw information from. If left empty, the AI writes entirely from its own knowledge."
                value={fields.sourceMaterial}
                onChange={v => set("sourceMaterial", v)}
                textarea
                rows={5}
              />
              {fields.sourceMaterial && (
                <div style={{ background: "#fffbf0", border: "1px solid #f0e0b0", borderRadius: 8, padding: "10px 14px", marginTop: -10, marginBottom: 20, fontFamily: "sans-serif", fontSize: 12, color: "#8a6d20" }}>
                  Source material detected — the AI will extract facts, angles and data from this and weave them into an original blog in your agency's voice.
                </div>
              )}
            </>
          )}

          {/* REWRITE mode */}
          {mode === MODES.REWRITE && (
            <>
              <Field label="Topic Override (optional)" placeholder="Leave blank to extract from the source blog" value={fields.topic} onChange={v => set("topic", v)} />
              <Field label="Target Audience (optional)" placeholder="Leave blank to extract from the source blog" value={fields.audience} onChange={v => set("audience", v)} />
              <Field label="Paste Reference Blog" placeholder="Paste the source blog here — the AI will use it as inspiration and write something completely original" value={fields.referenceBlog} onChange={v => set("referenceBlog", v)} textarea rows={8} required />
              <Field label="Additional Angles (optional)" placeholder="Any specific direction you want the new blog to take" value={fields.notes} onChange={v => set("notes", v)} textarea rows={3} />
            </>
          )}

          {/* REVISE mode */}
          {mode === MODES.REVISE && (
            <>
              <Field label="Paste Your Existing Blog" placeholder="Paste the blog you want to revise here" value={fields.originalBlog} onChange={v => set("originalBlog", v)} textarea rows={8} required />
              <Field label="What Would You Like Changed?" placeholder='Type exactly what you want revised — e.g. "The opening feels flat, make it more direct" or "Cut about 200 words from the middle"' value={fields.revisionInstructions} onChange={v => set("revisionInstructions", v)} textarea rows={4} required />
            </>
          )}

          {error && (
            <div style={{ background: "#fff4f4", border: "1px solid #f5c6c6", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontFamily: "sans-serif", fontSize: 13, color: "#c0392b" }}>
              {error}
            </div>
          )}

          <button
            onClick={handleGenerate}
            disabled={loading}
            style={{
              width: "100%", padding: "15px", background: loading ? "#888" : "#0ea5e9",
              color: "#fff", border: "none", borderRadius: 10, fontFamily: "sans-serif",
              fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
              letterSpacing: 0.3, transition: "background 0.15s", marginTop: 4,
            }}
          >
            {loading ? "Writing your blog..." : modeLabels[mode]}
          </button>
        </div>

        {/* Loading state */}
        {loading && (
          <div style={{ textAlign: "center", padding: "40px 0", fontFamily: "sans-serif", color: "#888", fontSize: 14 }}>
            <div style={{ width: 36, height: 36, border: "3px solid #e8e4dc", borderTop: "3px solid #38bdf8", borderRadius: "50%", margin: "0 auto 16px", animation: "spin 0.9s linear infinite" }} />
            Crafting your blog — this usually takes 20 to 40 seconds...
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Result */}
        {blog && (
          <div ref={resultRef}>
            {/* Blog output card */}
            <div style={{ background: "#fff", borderRadius: 14, padding: 36, boxShadow: "0 2px 16px rgba(0,0,0,0.07)", border: "1px solid #e8e4dc", marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
                <div style={{ fontFamily: "sans-serif", fontSize: 11, fontWeight: 700, color: "#0ea5e9", letterSpacing: 1.5, textTransform: "uppercase" }}>Your Blog</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    onClick={() => navigator.clipboard.writeText(rawOutput)}
                    style={{ padding: "9px 18px", background: "#f7f5f0", border: "1.5px solid #ddd", borderRadius: 8, fontFamily: "sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#444" }}
                  >
                    Copy Text
                  </button>
                  <button
                    onClick={() => downloadWord(blog)}
                    style={{ padding: "9px 18px", background: "#0ea5e9", border: "none", borderRadius: 8, fontFamily: "sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#fff" }}
                  >
                    Download Word Doc
                  </button>
                </div>
              </div>

              <h1 style={{ fontSize: 24, fontWeight: 700, color: "#1a1a2e", marginBottom: 10, lineHeight: 1.3 }}>{blog.title}</h1>

              <div style={{ background: "#f7f5f0", borderLeft: "3px solid #38bdf8", padding: "10px 16px", borderRadius: "0 8px 8px 0", marginBottom: 28, fontFamily: "sans-serif", fontSize: 13, color: "#666", lineHeight: 1.5 }}>
                <strong style={{ color: "#1a1a2e" }}>Meta:</strong> {blog.meta}
              </div>

              <div style={{ fontSize: 16, lineHeight: 1.85, color: "#2a2a2a" }}>
                {blog.body.split(/\n\n+/).map((para, i) => {
                  if (para.startsWith("## ") || para.startsWith("# ")) {
                    return <h2 key={i} style={{ fontSize: 19, fontWeight: 700, color: "#1a1a2e", margin: "28px 0 10px", lineHeight: 1.3 }}>{para.replace(/^#{1,2} /, "")}</h2>;
                  }
                  return <p key={i} style={{ marginBottom: 16 }}>{para}</p>;
                })}
              </div>
            </div>

            {/* Inline revision panel */}
            <InlineRevise
              rawOutput={rawOutput}
              language={fields.language}
              onRevised={(newRaw) => { setRawOutput(newRaw); setBlog(parseBlogOutput(newRaw)); }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function Label({ children }) {
  return <div style={{ fontFamily: "sans-serif", fontSize: 12, fontWeight: 700, color: "#444", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 7 }}>{children}</div>;
}

function Field({ label, placeholder, value, onChange, textarea, rows = 2, required }) {
  const base = {
    width: "100%", padding: "11px 14px", borderRadius: 9, border: "1.5px solid #ddd",
    fontSize: 14, fontFamily: "sans-serif", outline: "none", color: "#1a1a2e",
    background: "#fafafa", resize: "vertical", boxSizing: "border-box",
    transition: "border-color 0.15s",
  };
  return (
    <div style={{ marginBottom: 20 }}>
      <Label>{label}{required && <span style={{ color: "#38bdf8", marginLeft: 4 }}>*</span>}</Label>
      {textarea
        ? <textarea rows={rows} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} style={base} />
        : <input type="text" placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} style={{ ...base, resize: "none" }} />
      }
    </div>
  );
}

const selectStyle = {
  width: "100%", padding: "11px 14px", borderRadius: 9, border: "1.5px solid #ddd",
  fontSize: 14, fontFamily: "sans-serif", background: "#fafafa", color: "#1a1a2e",
  outline: "none", cursor: "pointer",
};
